/**
 * Token Service - Interface with the JINU token contract
 */
import { Connection, PublicKey, Transaction, Keypair, SystemProgram, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import { Program, BN } from '@project-serum/anchor';
import { 
  getConnection, 
  getProgram, 
  findPDA, 
  handleError
} from './baseService';
import { 
  TOKEN_PROGRAM_ID, 
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction
} from '@solana/spl-token';
import tokenIdl from '../idl/palm_heist_token.json';

// Constants for token
const TOTAL_SUPPLY = 100_000_000_000_000_000; // 100M JINU with 9 decimals
const TOKEN_DECIMALS = 9;
const JINU_TOKEN_PROGRAM_ID = new PublicKey('11111111111111111111111111111111');
const TOKEN_CONFIG_SEED = 'token_config';
const VESTING_SEED = 'vesting';

class TokenService {
  constructor() {
    this.connection = null;
    this.program = null;
    this.wallet = null;
    this.initialized = false;
    this.tokenMint = null;
    this.tokenDecimals = TOKEN_DECIMALS;
    this.tokenConfig = null;
  }

  /**
   * Initialize the token service
   * @param {Connection} connection - Solana connection
   * @param {Object} wallet - Solana wallet
   * @param {string} tokenMint - The token mint address
   * @returns {boolean} - Whether initialization was successful
   */
  async initialize(connection, wallet, tokenMint) {
    try {
      this.connection = connection;
      this.wallet = wallet;
      this.tokenMint = new PublicKey(tokenMint);
      
      // Load the program with the token IDL
      this.program = new Program(tokenIdl, JINU_TOKEN_PROGRAM_ID, {
        connection,
        wallet
      });
      
      // Find the token config PDA
      const [tokenConfigPDA, tokenConfigBump] = await findPDA(
        [Buffer.from(TOKEN_CONFIG_SEED)],
        JINU_TOKEN_PROGRAM_ID
      );
      this.tokenConfig = tokenConfigPDA;
      
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Error initializing token service:', error);
      return false;
    }
  }
  
  /**
   * Initialize the JINU token with the specified mint authority
   * @param {Keypair} mintKeypair - The keypair for the token mint
   * @returns {string} - Transaction signature
   */
  async initializeToken(mintKeypair) {
    if (!this.initialized) {
      throw new Error('Token service not initialized');
    }
    
    try {
      // Find the token config PDA
      const [tokenConfigPDA, tokenConfigBump] = await findPDA(
        [Buffer.from(TOKEN_CONFIG_SEED)],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Create token accounts for each pool
      const auctionPoolAccount = await getAssociatedTokenAddress(
        mintKeypair.publicKey,
        this.wallet.publicKey
      );
      
      const liquidityPoolAccount = await getAssociatedTokenAddress(
        mintKeypair.publicKey,
        this.wallet.publicKey
      );
      
      const rewardsPoolAccount = await getAssociatedTokenAddress(
        mintKeypair.publicKey,
        this.wallet.publicKey
      );
      
      const affiliatePoolAccount = await getAssociatedTokenAddress(
        mintKeypair.publicKey,
        this.wallet.publicKey
      );
      
      const reservePoolAccount = await getAssociatedTokenAddress(
        mintKeypair.publicKey,
        this.wallet.publicKey
      );
      
      // Initialize the token
      const tx = await this.program.methods
        .initializeToken(
          new BN(TOTAL_SUPPLY),
          TOKEN_DECIMALS,
          tokenConfigBump
        )
        .accounts({
          tokenConfig: tokenConfigPDA,
          mint: mintKeypair.publicKey,
          auctionPool: auctionPoolAccount,
          liquidityPool: liquidityPoolAccount,
          rewardsPool: rewardsPoolAccount,
          affiliatePool: affiliatePoolAccount,
          reservePool: reservePoolAccount,
          authority: this.wallet.publicKey,
          systemProgram: SystemProgram.programId,
          tokenProgram: JINU_TOKEN_PROGRAM_ID,
          rent: SYSVAR_RENT_PUBKEY
        })
        .signers([mintKeypair])
        .rpc();
      
      return tx;
    } catch (error) {
      return handleError('Error initializing token:', error);
    }
  }
  
  /**
   * Mint tokens to the auction pools
   * @returns {string} - Transaction signature
   */
  async mintAuctionTokens() {
    if (!this.initialized) {
      throw new Error('Token service not initialized');
    }
    
    try {
      // Find the token config PDA
      const [tokenConfigPDA, _] = await findPDA(
        [Buffer.from(TOKEN_CONFIG_SEED)],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Get pool accounts
      const auctionPoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      const liquidityPoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      const rewardsPoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      const affiliatePoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      const reservePoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      // Mint tokens to pools
      const tx = await this.program.methods
        .mintAuctionTokens()
        .accounts({
          tokenConfig: tokenConfigPDA,
          mint: this.tokenMint,
          auctionPool: auctionPoolAccount,
          liquidityPool: liquidityPoolAccount,
          rewardsPool: rewardsPoolAccount,
          affiliatePool: affiliatePoolAccount,
          reservePool: reservePoolAccount,
          authority: this.wallet.publicKey,
          tokenProgram: JINU_TOKEN_PROGRAM_ID
        })
        .rpc();
      
      return tx;
    } catch (error) {
      return handleError('Error minting auction tokens:', error);
    }
  }
  
  /**
   * Create a vesting schedule for a beneficiary
   * @param {PublicKey} beneficiaryPubkey - Beneficiary public key
   * @param {number} amount - Amount to vest
   * @param {number} startTimestamp - Start timestamp
   * @param {number} endTimestamp - End timestamp
   * @returns {string} - Transaction signature
   */
  async createVesting(beneficiaryPubkey, amount, startTimestamp, endTimestamp) {
    if (!this.initialized) {
      throw new Error('Token service not initialized');
    }
    
    try {
      // Find the token config PDA
      const [tokenConfigPDA, _] = await findPDA(
        [Buffer.from(TOKEN_CONFIG_SEED)],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Find the vesting PDA
      const [vestingPDA, vestingBump] = await findPDA(
        [Buffer.from(VESTING_SEED), beneficiaryPubkey.toBuffer()],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Get the reserve pool account
      const reservePoolAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        this.wallet.publicKey
      );
      
      // Get the vesting token account
      const vestingTokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        vestingPDA,
        true
      );
      
      // Get or create the beneficiary token account
      const beneficiaryTokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        beneficiaryPubkey
      );
      
      // Create the transaction
      const tx = new Transaction();
      
      // Add instruction to create the beneficiary token account if it doesn't exist
      tx.add(
        createAssociatedTokenAccountInstruction(
          this.wallet.publicKey,
          beneficiaryTokenAccount,
          beneficiaryPubkey,
          this.tokenMint
        )
      );
      
      // Add instruction to create the vesting
      tx.add(
        this.program.instruction.createVesting(
          new BN(amount),
          new BN(startTimestamp),
          new BN(endTimestamp),
          vestingBump,
          {
            accounts: {
              tokenConfig: tokenConfigPDA,
              vesting: vestingPDA,
              mint: this.tokenMint,
              reservePool: reservePoolAccount,
              vestingTokenAccount: vestingTokenAccount,
              beneficiary: beneficiaryPubkey,
              authority: this.wallet.publicKey,
              systemProgram: SystemProgram.programId,
              tokenProgram: JINU_TOKEN_PROGRAM_ID,
              associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
              rent: SYSVAR_RENT_PUBKEY
            }
          }
        )
      );
      
      // Send the transaction
      return await this.wallet.sendTransaction(tx, this.connection);
    } catch (error) {
      return handleError('Error creating vesting:', error);
    }
  }
  
  /**
   * Claim vested tokens
   * @param {PublicKey} beneficiaryPubkey - Beneficiary public key
   * @returns {string} - Transaction signature
   */
  async claimVestedTokens(beneficiaryPubkey) {
    if (!this.initialized) {
      throw new Error('Token service not initialized');
    }
    
    try {
      // Find the vesting PDA
      const [vestingPDA, _] = await findPDA(
        [Buffer.from(VESTING_SEED), beneficiaryPubkey.toBuffer()],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Get the vesting token account
      const vestingTokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        vestingPDA,
        true
      );
      
      // Get the beneficiary token account
      const beneficiaryTokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        beneficiaryPubkey
      );
      
      // Claim vested tokens
      const tx = await this.program.methods
        .claimVestedTokens()
        .accounts({
          vesting: vestingPDA,
          mint: this.tokenMint,
          vestingTokenAccount: vestingTokenAccount,
          beneficiaryTokenAccount: beneficiaryTokenAccount,
          beneficiary: beneficiaryPubkey,
          tokenProgram: JINU_TOKEN_PROGRAM_ID
        })
        .rpc();
      
      return tx;
    } catch (error) {
      return handleError('Error claiming vested tokens:', error);
    }
  }
  
  /**
   * Get token balance for a wallet
   * @param {PublicKey} walletPubkey - Wallet public key
   * @returns {number} - Token balance
   */
  async getTokenBalance(walletPubkey) {
    try {
      const tokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        walletPubkey
      );
      
      const balance = await this.connection.getTokenAccountBalance(tokenAccount);
      return balance.value.uiAmount;
    } catch (error) {
      console.error('Error getting token balance:', error);
      return 0;
    }
  }
  
  /**
   * Get vesting info for a beneficiary
   * @param {PublicKey} beneficiaryPubkey - Beneficiary public key
   * @returns {Object} - Vesting info
   */
  async getVestingInfo(beneficiaryPubkey) {
    if (!this.initialized) {
      throw new Error('Token service not initialized');
    }
    
    try {
      // Find the vesting PDA
      const [vestingPDA, _] = await findPDA(
        [Buffer.from(VESTING_SEED), beneficiaryPubkey.toBuffer()],
        JINU_TOKEN_PROGRAM_ID
      );
      
      // Get the vesting account info
      const vestingInfo = await this.program.account.vesting.fetch(vestingPDA);
      
      // Get the vesting token account
      const vestingTokenAccount = await getAssociatedTokenAddress(
        this.tokenMint,
        vestingPDA,
        true
      );
      
      // Get the vesting token balance
      const vestingBalance = await this.connection.getTokenAccountBalance(vestingTokenAccount);
      
      // Calculate the vested amount based on current time
      const now = Math.floor(Date.now() / 1000);
      const startTime = vestingInfo.startTimestamp.toNumber();
      const endTime = vestingInfo.endTimestamp.toNumber();
      const totalAmount = vestingInfo.totalAmount.toNumber();
      
      let vestedAmount = 0;
      if (now >= endTime) {
        // Fully vested
        vestedAmount = totalAmount;
      } else if (now > startTime) {
        // Partially vested
        const elapsed = now - startTime;
        const duration = endTime - startTime;
        vestedAmount = Math.floor((totalAmount * elapsed) / duration);
      }
      
      // Calculate the claimable amount
      const claimableAmount = vestedAmount - (totalAmount - vestingBalance.value.uiAmount);
      
      return {
        beneficiary: vestingInfo.beneficiary.toString(),
        totalAmount: totalAmount / Math.pow(10, this.tokenDecimals),
        startTimestamp: startTime,
        endTimestamp: endTime,
        vestedAmount: vestedAmount / Math.pow(10, this.tokenDecimals),
        claimableAmount: Math.max(0, claimableAmount / Math.pow(10, this.tokenDecimals))
      };
    } catch (error) {
      console.error('Error getting vesting info:', error);
      return null;
    }
  }
}

// Create a singleton instance
const tokenService = new TokenService();
export default tokenService;
